import { secondsToMilliseconds } from 'date-fns';

/**
 * How long a reader is assumed to still be reading after their last scroll or
 * touch. Past this, a post left open on screen stops counting.
 *
 * A minute rather than the more common thirty seconds: these answers are dense
 * clinical text, and on a desktop a whole short answer fits on one screen, so a
 * careful reader can go a long time without touching anything.
 */
export const READ_IDLE_TIMEOUT_MS = secondsToMilliseconds(60);

export type CommunityPostReadExitReason = 'left' | 'switched_post' | 'backgrounded';

export type CommunityPostReadResult = {
  activeSeconds: number;
  visibleSeconds: number;
  answerSeenPercent: number | null;
  commentsSeen: boolean | null;
};

/**
 * One continuous stretch of a reader looking at one post, from when it is on
 * screen to when it stops being on screen.
 *
 * Active time is credited per interaction rather than measured against a
 * running clock: each scroll or touch adds the time since the previous one, up
 * to the idle timeout. That way nothing has to tick, and a phone left on the
 * post adds at most one timeout's worth before the gap stops counting.
 *
 * How far down the reader got is measured against the end of the answer, not
 * the end of the page. Comments sit below the answer and vary from none to
 * dozens, so a page percentage would rank the same reading differently from
 * post to post. The answer's end can arrive after the first scrolls, because
 * layout settles late, so the percentage is worked out at the end.
 */
export function createCommunityPostReadSession(startedAt: number) {
  let lastActivityAt = startedAt;

  let activeMs = 0;

  let maxVisibleBottom = 0;

  let answerEndY: number | null = null;

  const creditActivityUntil = (now: number) => {
    activeMs += Math.min(Math.max(now - lastActivityAt, 0), READ_IDLE_TIMEOUT_MS);

    lastActivityAt = now;
  };

  return {
    recordActivity(now: number) {
      creditActivityUntil(now);
    },

    /** The lowest point of the page currently on screen, in content coordinates. */
    recordVisibleBottom(visibleBottom: number) {
      maxVisibleBottom = Math.max(maxVisibleBottom, visibleBottom);
    },

    recordAnswerEnd(y: number) {
      answerEndY = y;
    },

    finish(now: number): CommunityPostReadResult {
      creditActivityUntil(now);

      const durations = {
        activeSeconds: Math.round(activeMs / 1000),
        visibleSeconds: Math.round(Math.max(now - startedAt, 0) / 1000),
      };

      if (answerEndY === null || answerEndY <= 0) {
        return { ...durations, answerSeenPercent: null, commentsSeen: null };
      }

      return {
        ...durations,
        answerSeenPercent: Math.min(100, Math.round((maxVisibleBottom / answerEndY) * 100)),
        commentsSeen: maxVisibleBottom > answerEndY,
      };
    },
  };
}

export type CommunityPostReadSession = ReturnType<typeof createCommunityPostReadSession>;
